import {
  Component,
  ViewChild,
  AfterViewInit,
  effect,
  Injector,
  signal,
  computed,
  WritableSignal,
  Signal,
  inject,
} from '@angular/core';
import { NavService } from '@app/core/services/nav.service';
import { LayoutService } from '@app/core/services/layout.service';
import { Popover } from 'primeng/popover';
import { Button } from 'primeng/button';
import { UserService } from '@app/core/services/user.service';
import { Router } from '@angular/router';
import { PermissionService } from '@app/core/services/permission.service';

import { PermissionEnum } from '@app/generated/graphql';
import { FirstTimeLocalStorageService } from '@app/core/services/first-time-local-storage/first-time-local-storage.service';
import { NotificationsOrchestratorService } from './components/notifications-v2/notifications-orchestrator.service';
import { AlertsOrchestratorService } from './components/alerts-menu/alerts-orchestrator.service';

@Component({
  selector: 'app-global-layout',
  templateUrl: './global-layout.component.html',
  standalone: false,
})
export class GlobalLayoutComponent implements AfterViewInit {
  readonly CACHE_FEATURE_KEY = 'navigation';
  readonly CACHE_MECHANISM_KEY = 'popover-dismissed';

  PermissionEnum = PermissionEnum;

  private _firstTimeLocalStorageService = inject(FirstTimeLocalStorageService);
  public alertsOrchestratorService = inject(AlertsOrchestratorService);
  public notificationsOrchestratorService = inject(
    NotificationsOrchestratorService,
  );

  @ViewChild('firstTimeTarget')
  firstTimeTarget: Button;

  @ViewChild('firstTimePopover')
  firstTimePopover: Popover;

  firstTimePopoverDismissed: WritableSignal<boolean> = signal(false);
  showFirstTimePopover: Signal<boolean> = computed(
    () =>
      !this.firstTimePopoverDismissed() &&
      !this.navService.isSidebarOpen() &&
      this.navService.isNavigationEnabled(),
  );

  constructor(
    public navService: NavService,
    public layoutService: LayoutService,
    private injector: Injector,
    public userService: UserService,
    private router: Router,
    public permissionService: PermissionService,
  ) {
    this.initFirstTimePopover();
  }

  ngAfterViewInit(): void {
    // watch for sidebar state changes which are linked to the view being rendered
    this.onSidebarStateChange();
  }

  /**
   * Effect that watches the sidebar state and opens/closes the popover accordingly.
   */
  onSidebarStateChange(): void {
    // This effect triggers as a biproduct of the sidebar state change
    effect(
      () => {
        // if the popover is not initialized, exit the effect
        if (!this.firstTimePopover) {
          return;
        }

        if (this.showFirstTimePopover()) {
          this.openFirstTimePopover();
        } else {
          this.firstTimePopover.hide();
        }
      },
      // pass lifecycle injector to the effect for easy cleanup
      { injector: this.injector },
    );
  }

  /**
   * Initializes the first time popover by checking if the user has already dismissed it.
   */
  initFirstTimePopover(): void {
    if (
      !this._firstTimeLocalStorageService.isFirstTime(
        this.CACHE_FEATURE_KEY,
        this.CACHE_MECHANISM_KEY,
      )
    ) {
      this.firstTimePopoverDismissed.set(true);
    }
  }

  /**
   * Opens the first time popover.
   */
  openFirstTimePopover(): void {
    if (
      this.firstTimePopover &&
      this.firstTimeTarget &&
      !this.firstTimePopover.overlayVisible
    ) {
      this.firstTimePopover.show(null, this.firstTimeTarget.el.nativeElement);
    }
  }

  /**
   * Dismisses the first time popover.
   */
  dismissFirstTimePopover(): void {
    this.firstTimePopoverDismissed.set(true);
    this.firstTimePopover.hide();

    this._firstTimeLocalStorageService.setLocalStorage(
      this.CACHE_FEATURE_KEY,
      this.CACHE_MECHANISM_KEY,
    );
  }

  onSettings(): void {
    this.navService.openSidebar();
    this.navService.openSettings();
    this.router.navigate(['/account/settings']);
  }
}
